/**
 * Render an SVG string into a PNG Blob via Canvas.
 * Pulls the dog logo in as a data URL so the rendered PNG is self-contained.
 */

const SVG_NS = 'http://www.w3.org/2000/svg'

/** Convert a URL (e.g. /logo-256.png) to a base64 data URL. */
export async function urlToDataUrl(url: string): Promise<string> {
  const res = await fetch(url)
  const blob = await res.blob()
  return await new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

/** Convert an SVG string into a PNG Blob at the SVG's intrinsic size. */
export async function svgToPngBlob(
  svgString: string,
  width: number,
  height: number,
): Promise<Blob> {
  // Validate the SVG by parsing it. If invalid, this throws clearly.
  const parser = new DOMParser()
  const doc = parser.parseFromString(svgString, 'image/svg+xml')
  const err = doc.querySelector('parsererror')
  if (err) throw new Error('Failed to parse SVG: ' + err.textContent)

  const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
  const url = URL.createObjectURL(blob)

  try {
    const img = await loadImage(url)
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Could not get canvas 2D context')
    ctx.drawImage(img, 0, 0, width, height)
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('canvas.toBlob returned null'))),
        'image/png',
      )
    })
  } finally {
    URL.revokeObjectURL(url)
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = (e) => reject(e)
    img.src = src
  })
}

void SVG_NS

/** Try the Web Share API with file; fall back to triggering a download. */
export async function shareOrDownload(
  blob: Blob,
  filename: string,
  shareData: Partial<ShareData> = {},
): Promise<'shared' | 'downloaded'> {
  const file = new File([blob], filename, { type: blob.type })
  const data: ShareData = {
    files: [file],
    ...shareData,
  }
  if (
    typeof navigator !== 'undefined' &&
    typeof navigator.share === 'function' &&
    typeof navigator.canShare === 'function' &&
    navigator.canShare(data)
  ) {
    try {
      await navigator.share(data)
      return 'shared'
    } catch (err) {
      // User cancelled or share failed — fall through to download.
      if ((err as Error).name === 'AbortError') return 'shared'
    }
  }
  // Fallback: download the file.
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 1500)
  return 'downloaded'
}
